'use client';

import { useEffect, useRef } from 'react';
import type { Chart as ChartType, Plugin } from 'chart.js';
import { A2_ALPHA, A2_COLOR, NON_ALPHA, NON_COLOR, TOOLTIP_STYLE } from './chartTheme';
import ChartHeader from './ChartHeader';
import Logo from '@/components/icons/Logo';

export type PerfColor = 'gray' | 'red' | 'green' | 'yellow';

export type PerfBar = {
  label: string | string[];
  value: number;
  valueLabel: string;
  color: PerfColor;
};

const SOLID: Record<PerfColor, string> = {
  gray: NON_COLOR,
  red: '#E0513F',
  green: '#43A45C',
  yellow: A2_COLOR,
};

const FILL: Record<PerfColor, string> = {
  gray: NON_ALPHA(0.25),
  red: 'rgba(224,81,63,0.28)',
  green: 'rgba(67,164,92,0.28)',
  yellow: A2_ALPHA(0.25),
};

const LABEL: Record<PerfColor, string> = {
  gray: 'rgba(180,180,180,0.9)',
  red: '#E0513F',
  green: '#43A45C',
  yellow: A2_COLOR,
};

type PerfBarChartProps = {
  title: string;
  subtitle?: string;
  data: PerfBar[];
  yAxisTitle: string;
  yMax?: number;
  referenceLine?: { value: number; label: string };
  footer?: string;
  headerTitle?: string;
  headerDescription?: string;
};

export default function PerfBarChart({
  title,
  subtitle,
  data,
  yAxisTitle,
  yMax,
  referenceLine,
  footer,
  headerTitle,
  headerDescription,
}: PerfBarChartProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    let chart: ChartType | undefined;

    (async () => {
      const { Chart, registerables } = await import('chart.js');
      if (cancelled) return;
      Chart.register(...registerables);

      if (!canvasRef.current) return;

      const valueLabelPlugin: Plugin<'bar'> = {
        id: 'perfValueLabel',
        afterDatasetsDraw(c) {
          const ctx = c.ctx;
          const meta = c.getDatasetMeta(0);
          ctx.save();
          ctx.font = '13px Arial, sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'bottom';
          data.forEach((d, i) => {
            const bar = meta.data[i];
            if (!bar) return;
            const { x, y } = bar.getProps(['x', 'y'], true) as { x: number; y: number };
            ctx.fillStyle = LABEL[d.color];
            ctx.fillText(d.valueLabel, x, y - 7);
          });
          ctx.restore();
        },
      };

      const referenceLinePlugin: Plugin<'bar'> = {
        id: 'perfReferenceLine',
        afterDatasetsDraw(c) {
          if (!referenceLine) return;
          const ctx = c.ctx;
          const yAxis = c.scales.y;
          const { left, right } = c.chartArea;
          const y = yAxis.getPixelForValue(referenceLine.value);
          ctx.save();
          ctx.strokeStyle = 'rgba(255,255,255,0.55)';
          ctx.lineWidth = 1.5;
          ctx.setLineDash([6, 4]);
          ctx.beginPath();
          ctx.moveTo(left, y);
          ctx.lineTo(right, y);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.font = 'italic 12px Arial, sans-serif';
          ctx.fillStyle = 'rgba(255,255,255,0.7)';
          ctx.textAlign = 'right';
          ctx.textBaseline = 'bottom';
          ctx.fillText(referenceLine.label, right - 4, y - 4);
          ctx.restore();
        },
      };

      chart = new Chart(canvasRef.current, {
        type: 'bar',
        data: {
          labels: data.map((d) => d.label),
          datasets: [
            {
              data: data.map((d) => d.value),
              backgroundColor: data.map((d) => FILL[d.color]),
              borderColor: data.map((d) => SOLID[d.color]),
              borderWidth: 1.5,
              borderRadius: 3,
              maxBarThickness: 90,
              categoryPercentage: 0.7,
              barPercentage: 0.85,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: { duration: 800, easing: 'easeOutQuart' },
          interaction: { mode: 'nearest', axis: 'x', intersect: false },
          plugins: {
            legend: { display: false },
            tooltip: {
              ...TOOLTIP_STYLE,
              callbacks: {
                title: (items) => {
                  const l = data[items[0].dataIndex].label;
                  return Array.isArray(l) ? l.join(' ') : l;
                },
                label: (ctx) => data[ctx.dataIndex].valueLabel,
              },
            },
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: {
                font: { family: 'Arial', size: 13 },
                color: 'rgba(255,255,255,0.85)',
                autoSkip: false,
              },
            },
            y: {
              min: 0,
              max: yMax,
              grid: { color: 'rgba(128,128,128,0.15)' },
              ticks: {
                font: { family: 'Arial', size: 13 },
                color: 'rgba(255,255,255,0.85)',
              },
              title: {
                display: true,
                text: yAxisTitle,
                font: { family: 'Arial', size: 14 },
                color: 'rgba(255,255,255,0.9)',
              },
            },
          },
          layout: { padding: { top: 24 } },
        },
        plugins: [valueLabelPlugin, referenceLinePlugin],
      });
    })();

    return () => {
      cancelled = true;
      chart?.destroy();
    };
  }, [data, yAxisTitle, yMax, referenceLine]);

  return (
    <div className="w-full border-y border-[#373739] py-4 sm:rounded-2xl sm:border sm:p-8">
      {headerTitle && (
        <ChartHeader title={headerTitle} description={headerDescription ?? ''} />
      )}
      <div className="w-full rounded-2xl border border-[#373739] p-4 sm:p-6">
        <div className="text-lg font-bold text-white">{title}</div>
        {subtitle && <div className="mb-3 mt-1.5 text-base text-zinc-400">{subtitle}</div>}
        <div className="relative h-[320px] w-full max-[500px]:h-[260px]">
          <canvas ref={canvasRef} />
        </div>
        {footer && <div className="mt-4 text-sm text-zinc-500">{footer}</div>}
      </div>
      <div className="mt-6 flex justify-end">
        <Logo style={{ height: 20, width: 'auto' }} />
      </div>
    </div>
  );
}
